/**
 * Analytics Chart Color Palette
 *
 * Centralized color definitions for analytics charts.
 * For use with Chart.js, which doesn't support CSS variables (big sad).
 */

import { adjustColorLightness, interpolateColor } from '@/js/color.js';

export const ANALYTICS_COLORS = {
  blue: '#2196F3',
  green: '#0a9e65',
  orange: '#FF9800',
  purple: '#9C27B0',
  teal: '#009688',
  indigo: '#3F51B5',
  red: '#f24653',
  grey: '#E0E0E0',
};

const BASE_PALETTES = {
  categorical: [
    ANALYTICS_COLORS.blue,
    ANALYTICS_COLORS.green,
    ANALYTICS_COLORS.purple,
    ANALYTICS_COLORS.indigo,
    ANALYTICS_COLORS.orange,
    ANALYTICS_COLORS.teal,
  ],

  timing: [
    ANALYTICS_COLORS.indigo,
    ANALYTICS_COLORS.blue,
    ANALYTICS_COLORS.green,
    ANALYTICS_COLORS.orange,
    ANALYTICS_COLORS.red,
  ],
};

/**
 * Generate an array of colors for categorical data
 * Supports unlimited items by cycling through the palette with variations
 *
 * @param {number} count - Number of colors needed
 * @returns {Array<string>} Array of hex color values
 */
export function getCategoricalColors(count) {
  if (count <= 0) return [];

  const basePalette = BASE_PALETTES.categorical;
  const colors = [];

  for (let i = 0; i < count; i++) {
    const baseColorIndex = i % basePalette.length;
    const cycle = Math.floor(i / basePalette.length);

    if (cycle === 0) {
      // first cycle: base colors
      colors.push(basePalette[baseColorIndex]);
    } else if (cycle === 1) {
      // second cycle: lighter versions
      colors.push(adjustColorLightness(basePalette[baseColorIndex], 20));
    } else {
      // third+ cycles: darker versions
      colors.push(adjustColorLightness(basePalette[baseColorIndex], -20 * (cycle - 1)));
    }
  }

  return colors;
}

/**
 * Generate an array of colors for timing/progression data
 * Supports unlimited items by interpolating between colors
 * @param {number} count - Number of colors needed
 * @returns {Array<string>} Array of hex color values
 */
export function getTimingColors(count) {
  if (count <= 0) return [];
  if (count === 1) return [BASE_PALETTES.timing[0]];

  const basePalette = BASE_PALETTES.timing;

  // if we need fewer or equal colors than the base palette, just return a subset
  if (count <= basePalette.length) {
    const step = (basePalette.length - 1) / (count - 1);
    return Array.from({ length: count }, (_, i) => {
      const index = Math.round(i * step);
      return basePalette[index];
    });
  }

  // for more colors than we have, interpolate between base palette colors
  const colors = [];
  const segmentCount = basePalette.length - 1;
  const colorsPerSegment = Math.ceil((count - basePalette.length) / segmentCount);

  for (let i = 0; i < segmentCount; i++) {
    colors.push(basePalette[i]);

    // add interpolated colors between this color and the next
    const extraColors =
      i === segmentCount - 1
        ? count - colors.length - 1 // last segment gets remaining colors
        : colorsPerSegment;

    for (let j = 1; j <= extraColors && colors.length < count - 1; j++) {
      const ratio = j / (extraColors + 1);
      colors.push(interpolateColor(basePalette[i], basePalette[i + 1], ratio));
    }
  }

  colors.push(basePalette[basePalette.length - 1]);

  return colors.slice(0, count);
}

/**
 * Pre-defined color arrays for common chart types
 * For fixed-size arrays, use these. For dynamic sizes, use the generator functions above.
 */
export const CHART_COLOR_SETS = {
  binary: [ANALYTICS_COLORS.green, ANALYTICS_COLORS.orange],
  categorical: BASE_PALETTES.categorical,
  timing: BASE_PALETTES.timing,
};
